// 快斗 Blitz Duel — slice derivation (TECH_SPEC §4bis.28).
//
// A duel opens a share link on a ~12 s slice of the sender's run that ends one
// second past where they fell. `sliceForRun` decides where that slice starts.
//
// It is a **pure sim export**, not a server heuristic, for one reason: the
// server, the client and the probe must agree to the frame, and the only way to
// guarantee that is one deterministic integer function all three call. It is
// also what makes the verification design sound — the server recomputes the
// slice from the SOURCE run's own stored, already-verified input blob, so
// nothing the challenger sends can move the start frame (§4bis.28 "there is
// nothing to forge").
//
// No physics here. This module only *reads* a replay: it steps a `Sim` with the
// source's stream and asks what the body was doing at each candidate frame.

import {
  DUEL_CLEARANCE_FRAMES,
  DUEL_MIN_SOURCE_DEATH,
  DUEL_MIN_START_FRAME,
  DUEL_PREFIX_CAP,
  DUEL_SLICE_FRAMES,
  DUEL_SNAP_RANGE,
  DUEL_SURVIVE_FRAMES,
  HANDOFF_BUDGET_FRAMES_PER_EVENT,
  HANDOFF_RUNWAY_FRAMES,
  JUMP_AIRTIME_FRAMES,
  MAX_FRAMES,
  MAX_INPUT_EVENTS,
  PLAYER_START_X,
} from './constants';
import { distanceAtFrame, metersFromFQ } from './distance';
import { Sim } from './sim';
import { type InputEvent, type Obstacle } from './types';

/**
 * The two share verbs (§4bis.31), which are the SAME derivation differing in one
 * integer — see `sliceForRun`'s `ideal` table.
 *
 *   `challenge` 打败我 — "here is the 12 s that ended him". The slice ends one
 *               second past the sender's grave; you are asked to outlive it.
 *   `handoff`   帮我 — "here is where he fell, go on". The slice starts 4 s
 *               short of the grave; you are asked to carry the run further.
 *
 * The verb rides the LINK, never the run: one run can be offered either way, so
 * this is a property of the share ACT (§4bis.31 §4).
 */
export type SliceMode = 'challenge' | 'handoff';

export interface Slice {
  /** Which verb minted this slice. `challenge` for every pre-006 caller. */
  mode: SliceMode;
  /** Frame the challenger takes over on. The prefix is every source event
   *  before it; the sim's state at this frame is the body they inherit. */
  startFrame: number;
  /**
   * The line this duel has to PASS — alive at this frame ⇒ win. §4bis.30
   * amends its meaning: it is no longer where the run stops. The 活过了 seal
   * fires here as a mid-run beat and the challenger keeps running to a real
   * death, which is what the next slice derives from.
   */
  winFrame: number;
  /** Where the source's own run ended — their grave, the thing to survive. */
  sourceEndFrame: number;
  /** Absolute track meters at `startFrame` (the HUD reads absolute, §4bis.28). */
  startM: number;
  /** Absolute track meters the source reached. */
  sourceEndM: number;
  /** How many of the source's events fall before `startFrame`. */
  prefixCount: number;
}

export interface SliceOptions {
  /**
   * The source's end frame when the caller already knows it — a run's
   * `death_frame`, or `MAX_FRAMES` when it ended alive (finished or ran out of
   * track). Omitted = derive it by replaying to death / MAX_FRAMES.
   *
   * §4bis.30 retired 004's `endedAlive` companion: every stored stream now runs
   * to a natural end, so there is no run that stopped alive one frame short of
   * a death it never reached. A duel survivor's `death_frame` is a real death.
   */
  endFrame?: number;
  /**
   * Which verb is being offered (§4bis.31). Default `'challenge'`, so every
   * pre-006 call site derives byte-identically to before.
   */
  mode?: SliceMode;
}

/**
 * The events a hand-back recipient is GUARANTEED to be left (§4bis.31 §1).
 *
 * `DUEL_PREFIX_CAP = 150` is the wrong guard for a hand-back and this is the
 * whole reason the constant is not simply raised. That cap was written for a
 * challenge, whose prefix stops ~12 s before the source's grave; a hand-back's
 * prefix is everything before `E − 240`, so `prefixCount` approaches the whole
 * stream by design. Capping it would refuse exactly the late-track hand-backs
 * by input-dense humans that this iteration exists to create.
 *
 * So instead of capping the prefix, guarantee the SUFFIX: the recipient must be
 * left at least the global cap's own event density (`MAX_INPUT_EVENTS /
 * MAX_FRAMES` = 1 per 6 frames) over the frames that remain. A stream that
 * fails this spent faster than the global average over its own prefix, which is
 * precisely the case where the recipient would be squeezed; an honest stream
 * passes by construction.
 */
export function handoffMinBudget(startFrame: number): number {
  const remaining = Math.max(0, MAX_FRAMES - startFrame);
  return Math.ceil(remaining / HANDOFF_BUDGET_FRAMES_PER_EVENT);
}

/**
 * The frame each obstacle's action window opens: the earliest frame from which
 * an action started now still reaches it. Same rule the anti-cheat reaction
 * check uses (`server/src/verify.ts firstActionWindowFrame`), computed for the
 * whole track in one forward pass — the predicate is monotone in `f` (the
 * player advances and the jump's reach grows with speed) and obstacles are
 * x-sorted, so a single cursor is enough.
 */
export function actionWindowFrames(obstacles: readonly Obstacle[]): number[] {
  const out: number[] = new Array<number>(obstacles.length).fill(MAX_FRAMES);
  let i = 0;
  for (let f = 0; f <= MAX_FRAMES && i < obstacles.length; f++) {
    const playerX = (PLAYER_START_X + distanceAtFrame(f)) | 0;
    const ahead = f + JUMP_AIRTIME_FRAMES;
    const reach = (distanceAtFrame(ahead > MAX_FRAMES ? MAX_FRAMES : ahead) - distanceAtFrame(f)) | 0;
    for (;;) {
      const o = obstacles[i];
      if (o === undefined) break;
      if (playerX < o.x && o.x - playerX > reach) break;
      out[i] = f;
      i++;
    }
  }
  return out;
}

/** Per-frame handover eligibility of the source's body, plus where it ended. */
interface Replay {
  /** `grounded ∧ ¬sliding ∧ invulnFrames = 0` at the START of frame f. */
  ready: boolean[];
  /** Absolute track x at the start of frame f. */
  endFrame: number;
  endM: number;
}

/**
 * Replay the source, recording the state the challenger would inherit at each
 * frame. Snapshots are taken BEFORE the frame is stepped, which is exactly the
 * handover: replaying `inputs.filter(f < S)` leaves the sim at `frame === S`
 * with no frame-S input applied yet.
 */
function replaySource(
  seed: number,
  inputs: readonly InputEvent[],
  gen: string | undefined,
  stopAt: number,
): Replay {
  const sim = new Sim(seed, undefined, gen);
  const ready: boolean[] = [];
  let i = 0;
  for (let f = 0; f < stopAt; f++) {
    const s = sim.state;
    ready.push(s.grounded && !s.sliding && s.invulnFrames === 0);
    const start = i;
    while (i < inputs.length && inputs[i]!.f === f) i++;
    sim.step(start === i ? [] : inputs.slice(start, i));
    const after = sim.state;
    if (after.dead || after.finished) break;
  }
  const s = sim.state;
  return {
    ready,
    endFrame: s.dead ? s.deathFrame : s.frame,
    endM: Math.max(0, metersFromFQ((s.x - PLAYER_START_X) | 0)),
  };
}

/**
 * Derive the duel slice a run mints, or `null` — the honest failure, in which
 * case the link opens as an ordinary full run.
 *
 * Rules (§4bis.28, amended by §4bis.30 and §4bis.31):
 *   1. The source must have reached `DUEL_MIN_SOURCE_DEATH`, and the PREFIX the
 *      handover implies must leave a real budget — `DUEL_PREFIX_CAP` events for
 *      a challenge, `handoffMinBudget(S)` remaining events for a hand-back.
 *   2. `L = E + DUEL_SURVIVE_FRAMES` is the line to pass, and it must FIT on
 *      the track — a source that ended inside the last second mints nothing.
 *      **This is also what ends a hand-back volley**: a run that reached the
 *      finish has `E = MAX_FRAMES` and mints nothing, so the chain stops at the
 *      finish rather than looping.
 *   3. `S0 = max(DUEL_MIN_START_FRAME, L − DUEL_SLICE_FRAMES)` for a challenge;
 *      `max(DUEL_MIN_START_FRAME, E − HANDOFF_RUNWAY_FRAMES)` for a hand-back.
 *      The ONLY difference between the two verbs.
 *   4. Snap to the `S` nearest `S0` within ±`DUEL_SNAP_RANGE` where the source
 *      is grounded, not sliding and not invulnerable, and the next obstacle's
 *      action window opens ≥ `DUEL_CLEARANCE_FRAMES` later. Ties (equal
 *      distance either side of S0) resolve EARLIER — a longer slice.
 *
 * Grounded-and-not-sliding is not cosmetic: without it the challenger inherits
 * a mid-air body they never chose, and the 24-frame slide timer carries across
 * the handover.
 */
export function sliceForRun(
  seed: number,
  sourceInputs: readonly InputEvent[],
  gen?: string,
  opts: SliceOptions = {},
): Slice | null {
  const given = opts.endFrame;
  if (given !== undefined && (!Number.isInteger(given) || given < 0)) return null;
  // A run that ended on frame E traversed frame E, so the replay has to step
  // through it. One frame, one meter, and that meter is the duel's headline
  // number 「{name} 在 {d} 米倒下」.
  const cap = given === undefined ? MAX_FRAMES : Math.min(given + 1, MAX_FRAMES);
  const replay = replaySource(seed, sourceInputs, gen, cap);
  const sourceEndFrame = given === undefined ? replay.endFrame : Math.min(given, MAX_FRAMES);
  if (sourceEndFrame < DUEL_MIN_SOURCE_DEATH) return null;
  // The whole second has to FIT on the track (§4bis.30). A source that ended
  // within `DUEL_SURVIVE_FRAMES` of `MAX_FRAMES` — including one that finished,
  // whose end IS `MAX_FRAMES` — cannot pose the duel's question at all: there
  // is no "one second past where I fell" left to reach. Clamping instead would
  // mint a slice whose line equals its own grave: unwinnable unless you also
  // finish the track, margin always 0, and a seal that fires on the frame the
  // run ends. 004 refused these by accident (a finished run's `death_frame` is
  // -1, which `sliceForRun` rejected); §4bis.30's `sourceEnd` maps that to
  // MAX_FRAMES, so the refusal is now explicit.
  //
  // Measured honesty: this is DEFENCE-IN-DEPTH, not a live-bug fix. Sweeping
  // every end frame in the last second across the five live seeds and the
  // visual fixture, none of them mints even under 004's clamp — the late-track
  // clearance frontier already refuses them all. What it buys is that
  // `winFrame − sourceEndFrame == DUEL_SURVIVE_FRAMES` holds BY CONSTRUCTION
  // rather than by accident of track generation, and the overtake beat and the
  // seal beat depend on that gap being a full second to never share a window.
  if (sourceEndFrame + DUEL_SURVIVE_FRAMES > MAX_FRAMES) return null;

  const winFrame = sourceEndFrame + DUEL_SURVIVE_FRAMES;
  // §4bis.31: the ONE integer that separates the two verbs. Everything else —
  // `E`, `winFrame`, `legal`, the ±`DUEL_SNAP_RANGE` search with ties EARLIER,
  // the `DUEL_MIN_SOURCE_DEATH` gate, the last-second refusal above — is shared,
  // which is what makes a hand-back a duel whose prefix the server already knows
  // how to rebuild rather than a second derivation to keep in sync.
  //
  //   challenge  `winFrame − 720`  → 780 f to the line: "the 12 s that ended him"
  //   handoff    `E − 240`         → 300 f to the line: "where he fell — go on"
  const mode: SliceMode = opts.mode ?? 'challenge';
  const ideal =
    mode === 'handoff'
      ? Math.max(DUEL_MIN_START_FRAME, sourceEndFrame - HANDOFF_RUNWAY_FRAMES)
      : Math.max(DUEL_MIN_START_FRAME, winFrame - DUEL_SLICE_FRAMES);

  const sim = new Sim(seed, undefined, gen);
  const windows = actionWindowFrames(sim.obstacles);

  const legal = (s: number): boolean => {
    if (s < DUEL_MIN_START_FRAME || s >= winFrame) return false;
    if (s >= sourceEndFrame) return false; // the source must still be alive at the handover
    if (replay.ready[s] !== true) return false;
    const x = (PLAYER_START_X + distanceAtFrame(s)) | 0;
    for (let i = 0; i < sim.obstacles.length; i++) {
      const o = sim.obstacles[i]!;
      if (o.x + o.w <= x) continue; // already behind the body
      return windows[i]! - s >= DUEL_CLEARANCE_FRAMES;
    }
    return true; // nothing left on the track ahead
  };

  let startFrame = -1;
  for (let d = 0; d <= DUEL_SNAP_RANGE; d++) {
    if (legal(ideal - d)) {
      startFrame = ideal - d;
      break;
    }
    if (d > 0 && legal(ideal + d)) {
      startFrame = ideal + d;
      break;
    }
  }
  if (startFrame < 0) return null;

  let prefixCount = 0;
  while (prefixCount < sourceInputs.length && sourceInputs[prefixCount]!.f < startFrame) {
    prefixCount++;
  }
  // The mint guard, and it differs by verb (§4bis.30 / §4bis.31).
  //
  // Both answer the same question — "is the budget this slice leaves actually
  // enough?" — and both make `duelSuffixBudget(prefixCount)` a guarantee rather
  // than a hope. They differ because the two prefixes have different shapes: a
  // challenge's stops ~12 s before the grave and is measured tiny (p90 = 10
  // events on five live seeds), so a flat cap is free; a hand-back's is nearly
  // the whole stream BY DESIGN, so the same cap would refuse the deep chains
  // this verb exists to create. See `handoffMinBudget`.
  if (mode === 'handoff') {
    if (duelSuffixBudget(prefixCount) < handoffMinBudget(startFrame)) return null;
  } else if (prefixCount > DUEL_PREFIX_CAP) {
    return null;
  }

  return {
    mode,
    startFrame,
    winFrame,
    sourceEndFrame,
    startM: metersFromFQ(distanceAtFrame(startFrame)),
    sourceEndM: replay.endM,
    prefixCount,
  };
}

/**
 * How many events the challenger may still record (§4bis.30). 004 reserved a
 * flat `DUEL_SUFFIX_BUDGET = 120` because a duel stopped at its line; a duel
 * that runs to a real death is an ordinary run, so the right budget is the
 * ordinary one minus what the prefix already spent. ONE function, so the client
 * (which must never record a stream its own server would 422) and the server
 * (which enforces it) cannot drift.
 */
export function duelSuffixBudget(prefixCount: number): number {
  return Math.max(0, MAX_INPUT_EVENTS - prefixCount);
}

/** The prefix half of a hybrid duel stream: the source's own events before the
 *  handover. The server builds the verified stream as `prefix ++ suffix`; the
 *  client fast-forwards through exactly the same events. */
export function duelPrefix(
  sourceInputs: readonly InputEvent[],
  startFrame: number,
): InputEvent[] {
  const out: InputEvent[] = [];
  for (const e of sourceInputs) {
    if (e.f >= startFrame) break;
    out.push(e);
  }
  return out;
}
